import React, { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { downloadBillingCycle837Edi } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { getUserFacingApiMessageAsync } from '../../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../../utils/downloadFile';

const EDI_ERROR_FALLBACK =
  'Unexpected error occurred. Please try again or contact support';

export interface BillingCycle837EdiDownloadButtonProps {
  billingCycleId: string;
  /** When set, render as a full-width labeled menu row instead of an icon-only button. */
  label?: string;
}

export const BillingCycle837EdiDownloadButton: React.FC<
  BillingCycle837EdiDownloadButtonProps
> = ({ billingCycleId, label }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { error: showError } = useToast();

  const handleClick = async () => {
    setIsDownloading(true);
    try {
      const resp = await downloadBillingCycle837Edi({
        path: { billing_cycle_id: billingCycleId },
        responseType: 'blob',
      });
      if (resp.error) {
        console.error('Failed to download 837I EDI:', resp);
        showError(
          'Failed to download EDI file',
          await getUserFacingApiMessageAsync(resp, EDI_ERROR_FALLBACK)
        );
        return;
      }
      if (!resp.data) {
        console.error('Failed to download 837I EDI: no data returned');
        showError('Failed to download EDI file', EDI_ERROR_FALLBACK);
        return;
      }
      downloadResponseAsFile(resp, `837i_${billingCycleId}.edi`);
    } catch (error) {
      console.error('Failed to download 837I EDI:', error);
      showError(
        'Failed to download EDI file',
        await getUserFacingApiMessageAsync(error, EDI_ERROR_FALLBACK)
      );
    } finally {
      setIsDownloading(false);
    }
  };

  if (label) {
    return (
      <Button
        type='button'
        variant='ghost'
        onClick={() => void handleClick()}
        disabled={isDownloading}
        className='w-full justify-start gap-2 text-muted-foreground hover:text-foreground'
        aria-label={label}
        title='Download the 837I electronic claim file (EDI)'
      >
        {isDownloading ? (
          <Loader2 className='h-4 w-4 animate-spin' />
        ) : (
          <FileDown className='h-4 w-4' />
        )}
        {label}
      </Button>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={() => void handleClick()}
            disabled={isDownloading}
            className='text-muted-foreground hover:text-foreground'
            aria-label='Download 837I EDI file'
          >
            {isDownloading ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <FileDown className='h-4 w-4' />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom' className='text-xs'>
          Download 837I EDI file
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
